'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

let _ = t => t,
    _t;

import { CALYPSO, EERIE, OZ } from 'HubStyleTokens/colors';
import FormattedMessage from 'I18n/components/FormattedMessage';
import * as PurchaseMotionsLocations from 'pricing-pages-lib/constants/PurchaseMotionsLocations';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { getOfferIdFromIdByUnitScopeRecord } from 'pricing-pages-lib/utils/offerUtils';
import { getProductVersion } from 'pricing-pages-lib/utils/productsUtils';
import { getGAClassName } from 'pricing-pages-lib/utils/publicPageUrls';
import { useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ANNUAL, MONTHLY } from 'self-service-api/constants/Terms';
import styled from 'styled-components';
import { BUY_TOUCHLESS } from 'ui-addon-upgrades/_core/purchaseMotions/PurchaseMotionTypes';
import UICardWrapper from 'UIComponents/card/UICardWrapper';
import Small from 'UIComponents/elements/Small';
import UILink from 'UIComponents/link/UILink';
import UIHelpLink from 'UIComponents/tooltip/UIHelpLink';
import { activeCurrencySelector } from '../../selectors/CurrencySelectors';
import { hasPricingAndPurchasingSucceededSelector } from '../../selectors/LoadingStateSelectors';
import { commonAvailableTermsSelector, commonPaymentTermsSelector, offerInfoSelector } from '../../selectors/OfferSelectors';
import { calculatedPriceSelector, planAdditionalCostSelector, priceBreakdownSelector } from '../../selectors/PricingSelectors';
import { configurationsWithPBORequiredOnboardingSelector, offerProductConfigurationsSelector } from '../../selectors/ProductConfigsSelectors';
import { hasProductVersionInProductInventorySelector, hasSkuInProductInventorySelector, ownsCrmSuiteProductLevel } from '../../selectors/ProductInventorySelectors';
import { crmSuiteProductLevelSelector, productForSkuIdSelector, productVersionSelector, skuIdsContainOnlyCrmSuite, skuSelector } from '../../selectors/ProductsSelectors';
import { commonPurchaseMotionsSelector, suitePurchaseMotionsMapSelector } from '../../selectors/PurchaseMotionSelectors';
import { requiredOnboardingSkuIdSelector } from '../../selectors/RequiredOnboardingSelectors';
import { productDataSelector } from '../../selectors/SelectorUtils';
import breakpoint from '../../utils/responsiveBreakpoints';
import DetailedTermSelector from '../common/DetailedTermSelector';
import SkeletonLoadWrapper from '../loading/SkeletonLoadWrapper';
import { AsyncPricingBreakdownModal } from '../price-breakdown-modal/AsyncPricingBreakdownModal';
import CRMSuiteAlert from './CRMSuiteAlert';
import EmptyShoppingCart from './EmptyShoppingCart';
import { PlanBuilderContext } from './PlanBuilder';
import PlanBuilderCTAsContainer from './PlanBuilderCTAsContainer';
import { PriceDisplaySection } from './PriceDisplaySection';
import RequiredOnboardingSubtextContainer from './RequiredOnboardingSubtextContainer';
import ShareLink from './ShareLink';
import ShoppingCart from './ShoppingCart';
const StyledSummaryColumnWrapper = styled(UICardWrapper).withConfig({
  displayName: "SummaryColumn__StyledSummaryColumnWrapper",
  componentId: "v0osi4-0"
})(["overflow:hidden;"]);
const StyledGradientBar = styled.div.withConfig({
  displayName: "SummaryColumn__StyledGradientBar",
  componentId: "v0osi4-1"
})(["height:3px;background-color:#00bda5;background-image:linear-gradient( -303deg,", ",0%,#00afb2 56%,", " 100% );"], CALYPSO, OZ);
const PriceBreakdownLink = styled(UILink).withConfig({
  displayName: "SummaryColumn__PriceBreakdownLink",
  componentId: "v0osi4-2"
})(["", ";"], breakpoint.tablet(_t || (_t = _`
    font-size: 14px;
  `)));
const RequiredOnboardingWrapper = styled.div.withConfig({
  displayName: "SummaryColumn__RequiredOnboardingWrapper",
  componentId: "v0osi4-3"
})(["text-align:center;&:not(:empty){margin-top:12px;}"]);
const StyledSubtextHelpLink = styled(UIHelpLink).withConfig({
  displayName: "SummaryColumn__StyledSubtextHelpLink",
  componentId: "v0osi4-4"
})(["color:", ";"], EERIE);

const SummaryColumn = ({
  className,
  shareLinkAnchor,
  hideCrmSuiteAlert = false,
  hubsSignupProductOverride
}) => {
  const {
    queryParamTerm,
    setQueryParamTerm,
    productConfigurations,
    setProductConfigurations,
    gaTrackingId,
    requiredProductLines,
    checkoutProductConfigurations,
    trackingSource
  } = useContext(PlanBuilderContext);
  const hasPricingAndPurchasingSucceeded = useSelector(hasPricingAndPurchasingSucceededSelector);
  const skuIds = useMemo(() => productConfigurations.map(({
    skuId
  }) => skuId), [productConfigurations]);
  const productNames = useSelector(state => skuIds.map(skuId => productForSkuIdSelector(state, {
    skuId
  }).apiName));
  const requiredSkuIds = useSelector(state => skuIds.filter(skuId => {
    const product = productForSkuIdSelector(state, {
      skuId
    });
    return product.productLevel && requiredProductLines.includes(product.productLine);
  }));
  const [isPriceBreakdownVisible, setIsPriceBreakdownVisible] = useState(false);
  const activeCurrencyCode = useSelector(activeCurrencySelector);
  const hasMultipleTerms = useSelector(state => commonAvailableTermsSelector(state, {
    skuIds: productConfigurations.map(({
      skuId
    }) => skuId)
  }).length > 1);
  const suiteProductLevel = useSelector(state => crmSuiteProductLevelSelector(state, {
    skuIds: productConfigurations.map(({
      skuId
    }) => skuId)
  }));
  const hasOnlySuiteSkuIds = useSelector(state => skuIdsContainOnlyCrmSuite(state, {
    skuIds: productConfigurations.map(({
      skuId
    }) => skuId)
  }));
  const selectedOwnedSuiteProductLevel = useSelector(state => ownsCrmSuiteProductLevel(state, {
    productLevel: suiteProductLevel
  })); // We exclude skuds with PBOs from checkoutProductConfigurations, so need to add them back for the pricing breakdown modal

  const productConfigurationsForPriceDisplays = useSelector(state => configurationsWithPBORequiredOnboardingSelector(state, {
    productConfigurations: checkoutProductConfigurations
  }));
  const price = useSelector(state => suiteProductLevel && !selectedOwnedSuiteProductLevel ? calculatedPriceSelector(state, {
    productConfigurations: productConfigurationsForPriceDisplays,
    queryParamTerm
  }) : planAdditionalCostSelector(state, {
    productConfigurations: productConfigurationsForPriceDisplays,
    queryParamTerm
  }));
  const ownsAnyBaseHubs = useSelector(state => checkoutProductConfigurations.some(({
    skuId
  }) => {
    return hasProductVersionInProductInventorySelector(state, {
      skuId: productVersionSelector(state, {
        skuId
      }).skuId
    });
  }));
  const priceBreakdown = useSelector(state => priceBreakdownSelector(state, {
    productConfigurations: productConfigurationsForPriceDisplays,
    queryParamTerm,
    adjustPricesForOwnership: true
  }));
  const paymentTerm = useSelector(state => {
    const offerProductConfigurations = offerProductConfigurationsSelector(state, {
      productConfigurations,
      queryParamTerm
    });
    const {
      offerId: firstAvailableOfferId
    } = offerProductConfigurations.find(({
      offerId: offerIdInConfig
    }) => offerIdInConfig) || {
      offerId: null
    }; // This should always pass when `offerProductConfigurations` is non-empty.

    const areAllPaymentTermsSame = offerProductConfigurations.every(({
      offerId: offerIdInConfig
    }) => [offerInfoSelector(state, {
      offerId: getOfferIdFromIdByUnitScopeRecord(offerIdInConfig)
    }).payment === offerInfoSelector(state, {
      offerId: getOfferIdFromIdByUnitScopeRecord(firstAvailableOfferId)
    }).payment]);
    const offerIdToUse = areAllPaymentTermsSame ? firstAvailableOfferId : null; // If all offer ids have the same payment term, return that payment term

    if (offerIdToUse) return offerInfoSelector(state, {
      offerId: getOfferIdFromIdByUnitScopeRecord(offerIdToUse)
    }).payment; // Returns the lowest common payment term if query param term is invalid

    const commonPaymentTerms = commonPaymentTermsSelector(state, {
      skuIds: productConfigurations.map(({
        skuId
      }) => skuId)
    });
    if (!commonPaymentTerms || !commonPaymentTerms.length) return MONTHLY;
    if (!commonPaymentTerms.includes(queryParamTerm)) return commonPaymentTerms[0];
    return queryParamTerm;
  });
  const productData = useSelector(productDataSelector);

  const onRemove = skuIdToRemove => {
    setProductConfigurations(() => {
      return productConfigurations.filter(({
        skuId
      }) => {
        return getProductVersion({
          productData
        }, {
          skuId: skuIdToRemove
        }).skuId !== getProductVersion({
          productData
        }, {
          skuId
        }).skuId;
      });
    });
  };

  const hasRequiredOnboarding = useSelector(state => skuIds.some(skuId => requiredOnboardingSkuIdSelector(state, {
    skuId
  })));
  const purchaseMotions = useSelector(state => {
    if (suiteProductLevel && hasOnlySuiteSkuIds) {
      return suitePurchaseMotionsMapSelector(state)[suiteProductLevel] || [];
    } // This helps cover the case when we are just buying additional contacts / users


    const ownsAllItemsInCart = checkoutProductConfigurations.every(({
      skuId
    }) => hasSkuInProductInventorySelector(state, {
      skuId
    }));
    const skuIdsToUse = checkoutProductConfigurations.map(({
      skuId
    }) => skuId).filter(skuId => {
      const isQuantifiable = skuSelector(state, {
        skuId
      }).quantifiable;
      return isQuantifiable || !hasSkuInProductInventorySelector(state, {
        skuId
      }) || ownsAllItemsInCart;
    });
    const commonPurchaseMotions = commonPurchaseMotionsSelector(state, {
      skuIds: skuIdsToUse,
      location: PurchaseMotionsLocations.CALCULATOR
    }); // e.g. use case - HSFX-eligible products and starter products don't have any
    // common terms, so we cannot send them to checkout touchlessly.

    const hasCommonTerms = !!commonAvailableTermsSelector(state, {
      skuIds: skuIdsToUse
    }).length;

    if (!hasCommonTerms) {
      return commonPurchaseMotions.filter(purchaseMotion => purchaseMotion !== BUY_TOUCHLESS);
    }

    return commonPurchaseMotions;
  });

  if (!checkoutProductConfigurations.length) {
    return /*#__PURE__*/_jsxs(StyledSummaryColumnWrapper, {
      className: className,
      flush: true,
      children: [/*#__PURE__*/_jsx(StyledGradientBar, {}), /*#__PURE__*/_jsx(EmptyShoppingCart, {})]
    });
  }

  const isCheckingOutAnyProducts = checkoutProductConfigurations.length && checkoutProductConfigurations.some(({
    quantity
  }) => quantity > 0);

  const handlePriceBreakdownLinkClick = () => {
    getUsageTracker().track('clickToViewModal', {
      name: 'pricing-breakdown-modal',
      location: trackingSource,
      productNames,
      skuIds
    });
    setIsPriceBreakdownVisible(true);
  };

  return /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsxs(StyledSummaryColumnWrapper, {
      className: className,
      flush: true,
      children: [/*#__PURE__*/_jsx(StyledGradientBar, {}), /*#__PURE__*/_jsxs("div", {
        className: "p-x-8 p-y-4 flex-column",
        children: [hasMultipleTerms && /*#__PURE__*/_jsx("div", {
          className: "m-bottom-4",
          children: /*#__PURE__*/_jsx(DetailedTermSelector, {
            trackingSource: trackingSource,
            activeTerm: queryParamTerm,
            onChange: setQueryParamTerm,
            skuIds: productConfigurations.map(({
              skuId
            }) => skuId)
          })
        }), /*#__PURE__*/_jsx("div", {
          style: {
            textAlign: 'center'
          },
          className: "m-bottom-4",
          children: /*#__PURE__*/_jsx(PriceDisplaySection, {
            monthlyCostPrice: price.monthly.calculatedPrice,
            annualListPrice: price.annually.fullPrice,
            activeCurrencyCode: activeCurrencyCode,
            annualCostPrice: price.annually.calculatedPrice,
            term: paymentTerm,
            costToGetStarted: price.oneTimeFee.calculatedPrice + (paymentTerm === ANNUAL ? price.annually.calculatedPrice : price.monthly.calculatedPrice),
            showTotalPriceAsterisk: hasRequiredOnboarding,
            showingOnlyAdditionalCost: ownsAnyBaseHubs && !suiteProductLevel || selectedOwnedSuiteProductLevel
          })
        }), /*#__PURE__*/_jsx(ShoppingCart, {
          productConfigurations: selectedOwnedSuiteProductLevel ? checkoutProductConfigurations : productConfigurations,
          onRemove: onRemove,
          className: "m-bottom-4",
          requiredSkuIds: requiredSkuIds,
          queryParamTerm: queryParamTerm
        }), /*#__PURE__*/_jsx("div", {
          className: "flex-column align-center justify-center m-bottom-4",
          children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
            isLoading: !hasPricingAndPurchasingSucceeded,
            Content: PlanBuilderCTAsContainer,
            contentProps: {
              productConfigurations: checkoutProductConfigurations,
              queryParamTerm,
              ctaStyles: {
                display: 'block',
                minWidth: 200,
                width: '100%',
                lineHeight: '24px',
                paddingTop: 16
              },
              trackingSource,
              hubsSignupProductOverride,
              purchaseMotions
            }
          })
        }), !hideCrmSuiteAlert && /*#__PURE__*/_jsx("div", {
          children: /*#__PURE__*/_jsx(CRMSuiteAlert, {
            suiteProductLevel: suiteProductLevel
          })
        }), isCheckingOutAnyProducts && /*#__PURE__*/_jsx("div", {
          className: "justify-center",
          children: /*#__PURE__*/_jsx(PriceBreakdownLink, {
            onClick: handlePriceBreakdownLinkClick,
            className: getGAClassName(`${gaTrackingId}-pricing-breakdown`),
            onMouseEnter: () => AsyncPricingBreakdownModal.preload(),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "pricingPage.planBuilder.viewPriceBreakdownLink",
              className: getGAClassName(`${gaTrackingId}-pricing-breakdown`)
            })
          })
        }), shareLinkAnchor !== undefined && isCheckingOutAnyProducts && /*#__PURE__*/_jsx("div", {
          className: "justify-center m-top-4",
          children: /*#__PURE__*/_jsx(ShareLink, {
            anchorId: shareLinkAnchor,
            trackingSource: trackingSource
          })
        }), /*#__PURE__*/_jsx(RequiredOnboardingWrapper, {
          children: /*#__PURE__*/_jsx(RequiredOnboardingSubtextContainer, {
            queryParamTerm: queryParamTerm,
            skuIds: skuIds
          })
        })]
      }), isPriceBreakdownVisible && /*#__PURE__*/_jsx(AsyncPricingBreakdownModal, {
        onCloseClick: () => setIsPriceBreakdownVisible(false),
        priceBreakdown: priceBreakdown,
        paymentTerm: paymentTerm
      })]
    }), /*#__PURE__*/_jsx("div", {
      className: "m-top-2 text-center",
      children: /*#__PURE__*/_jsx(StyledSubtextHelpLink, {
        content: {
          body: /*#__PURE__*/_jsxs("div", {
            className: "p-all-5",
            children: [/*#__PURE__*/_jsx(FormattedMessage, {
              message: "pricingPage.feeIncreasesProductServicesCatalog"
            }), /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(FormattedMessage, {
              message: "pricingPage.annualDiscountLegalDisclaimer"
            })]
          })
        },
        children: /*#__PURE__*/_jsx(Small, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "pricingPage.planBuilder.priceDisclaimer"
          })
        })
      })
    })]
  });
};

export default SummaryColumn;